import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { Button } from '#components/atoms/buttons/Button.tsx';
import { ErrorMessage, HelpText, Input } from '#components/atoms/forms/index.ts';
import { Field } from '#components/patterns/forms/field/Field.tsx';
import { Modal, ModalBody, ModalFooter, ModalHeader } from '#components/patterns/modal/index.ts';
import { privateCidrRequiredSchema } from '#schemas/general/cidr.ts';

const FORM_ID = 'get-started-ipv4-range';

const ipv4RangeSchema = z.object({ cidr: privateCidrRequiredSchema });

type Props = {
  /** Pre-fills the input so reopening to edit an already-added range starts from the current value. */
  defaultCidr?: string;
  /** Called with the validated CIDR when the user commits the range. */
  onAdd: (cidr: string) => void;
  onClose: () => void;
};

/**
 * Collects a single private IPv4 CIDR for the get-started network. Validates inside the modal
 * (so an invalid range can't be committed) and hands the valid value back via `onAdd`, mirroring
 * the routes `AddCIDRModal` / `RoutableCIDRForm` pattern.
 */
export function AddIpv4RangeModal({ defaultCidr = '', onAdd, onClose }: Props) {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(ipv4RangeSchema),
    defaultValues: { cidr: defaultCidr },
  });

  // The modal sits inside the parent's <form> in the React tree, so a bare submit would bubble up
  // and trigger the network-create form. stopPropagation keeps it scoped to this form (mirrors
  // RoutableCIDRForm).
  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();
    return handleSubmit(({ cidr }) => onAdd(cidr))(e);
  };

  return (
    <Modal isOpen={true} onClose={onClose}>
      <ModalHeader>Add an IPv4 range</ModalHeader>
      <ModalBody>
        <form id={FORM_ID} onSubmit={onSubmit}>
          <Field id="get-started-ipv4" label="IPv4 address range">
            <HelpText>Use CIDR notation (e.g. 100.100.0.0/22)</HelpText>
            <Input className="width-100" placeholder="100.100.0.0/22" {...register('cidr')} />
            {errors.cidr ?
              <ErrorMessage>{errors.cidr.message}</ErrorMessage>
            : null}
          </Field>
        </form>
      </ModalBody>
      <ModalFooter>
        <div className="flex gap-8">
          <Button type="button" variant="link" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" form={FORM_ID}>
            Add range
          </Button>
        </div>
      </ModalFooter>
    </Modal>
  );
}
